import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { processOidcCallback, createSsoUser } from 'API/auth'
import { useStores } from './useStores'

export const useSSO = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { authStore } = useStores()

  useEffect(() => {
    const handleSSOCallback = async () => {
      const urlParams = new URLSearchParams(location.search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      const error = urlParams.get('error')
      const auth = urlParams.get('auth')
      const token = urlParams.get('token')
      const provider = urlParams.get('provider')

      // Handle SSO errors
      if (error || auth === 'error') {
        const errorMessage = error || urlParams.get('message') || 'Unknown SSO error'
        console.error('❌ SSO Error:', errorMessage)
        toast.error(`SSO Authentication failed: ${errorMessage}`)
        
        // Clear SSO data and redirect to login
        clearSSOData()
        navigate('/login', { replace: true })
        return
      }

      // Handle direct token success (backend already processed the OIDC flow)
      if (auth === 'success' && token) {
        console.log('🔄 Processing SSO token callback...')
        console.log('═══════════════════════════════════════════════════════════')
        console.log('📋 Token Callback Analysis:')
        console.log('  - Current URL:', window.location.href)
        console.log('  - Token received:', token.substring(0, 50) + '...')
        console.log('  - Provider:', provider)

        try {
          // Store the token and get user info
          authStore.setAccessToken(token)
          
          // Get user details using the token
          await authStore.getMyUser()
          
          // Clear SSO callback data since we're done
          clearSSOData()
          
          // Clean the URL
          window.history.replaceState({}, document.title, window.location.pathname)
          
          console.log('✅ SSO token authentication successful!')
          toast.success('Successfully logged in via SSO!')
          
          // Navigate to the main application
          navigate('/dashboard', { replace: true })
          
        } catch (error: any) {
          console.error('❌ Token processing failed:', error)
          toast.error(`Login failed: ${error.message || 'Token processing failed'}`)
          
          // Clear invalid data and redirect to login
          clearSSOData()
          navigate('/login', { replace: true })
        }
        
        return
      }

      // Handle SSO success callback
      if (code && state) {
        console.log('🔄 Processing SSO callback...')
        
        try {
          console.log('🔄 Processing SSO Callback...')
          console.log('═══════════════════════════════════════════════════════════')
          console.log('📋 Callback Analysis:')
          console.log('  - Current URL:', window.location.href)
          console.log('  - Code received:', code.substring(0, 20) + '...')
          console.log('  - State received:', state)

          // Verify state parameter
          const storedState = localStorage.getItem('sso-state')
          const storedOrgId = localStorage.getItem('sso-org-id')
          const storedProvider = localStorage.getItem('sso-provider') || 'oidc'
          const autoCreate = localStorage.getItem('sso-auto-create') === 'true'

          console.log('🔍 Stored callback data:')
          console.log('  - Stored State:', storedState)
          console.log('  - Organization ID:', storedOrgId)
          console.log('  - Provider:', storedProvider)
          console.log('  - Auto-create enabled:', autoCreate)

          // Backend modifies state by appending '-demo' or '-app', so we need to validate the base state
          let baseReceivedState = state
          if (state.endsWith('-demo') || state.endsWith('-app')) {
            // Remove the backend-added suffix to get the original state
            baseReceivedState = state.replace(/-(?:demo|app)$/, '')
          }
          
          console.log('🔍 State validation:')
          console.log('  - Received state from callback:', state)
          console.log('  - Base state (after removing suffix):', baseReceivedState)
          console.log('  - Stored original state:', storedState)

          if (baseReceivedState !== storedState) {
            throw new Error(`Invalid state parameter - possible security issue. Expected: ${storedState}, Got: ${baseReceivedState}`)
          }

          if (!storedOrgId) {
            throw new Error('Missing organization ID')
          }

          const organizationId = parseInt(storedOrgId, 10)

          console.log('🌐 Complete SSO Flow:')
          console.log('  1. ✅ User clicked SSO button in React app')
          console.log('  2. ✅ React app → API Authorization URL')
                console.log('  3. ✅ API → OIDC Provider Discovery & Auth URL')
      console.log('  4. ✅ User → OIDC Provider login page (credentials entered)')
      console.log('  5. ✅ OIDC Provider → API callback with auth code')
          console.log('  6. 🔄 API → Processing token & user info...')
          console.log('  7. 🔄 API → React app callback (current step)')
          console.log('  8. ⏳ React app → Process authentication...')

          try {
            // Try normal SSO callback first
            console.log('🔄 Calling processOidcCallback API...')
            console.log('  - Endpoint: POST /auth/oidc/callback')
            console.log('  - Payload:', { code: code.substring(0, 20) + '...', state, organizationId })
            
            const loginResponse = await processOidcCallback(code, state, organizationId, storedProvider)

            console.log('✅ NORMAL SSO LOGIN SUCCESSFUL!')
            console.log('  - User Info:', loginResponse.userInfo)
            console.log('  - Token received:', loginResponse.token.substring(0, 20) + '...')

            // Store authentication data (similar to the existing login method)
            authStore.accessToken = loginResponse.token
            authStore.userDetail = loginResponse.userInfo
            localStorage.setItem('ACCESS_TOKEN', loginResponse.token)

            console.log('💾 Authentication data stored in app state and localStorage')
            toast.success(`Welcome ${loginResponse.userInfo.firstName}! SSO login successful.`)

            // Clear SSO temporary data
            clearSSOData()
            console.log('🧹 Cleared SSO temporary data from localStorage')

            // Redirect to dashboard
            console.log('🏠 Redirecting to dashboard...')
            console.log('═══════════════════════════════════════════════════════════')
            navigate('/dashboard', { replace: true })

          } catch (callbackError: any) {
            console.log('❌ SSO Callback failed:', callbackError.message)
            
            // If auto-create is enabled and user not found, try to create user
            if (autoCreate && callbackError.message?.includes('No user found with email')) {
              console.log('🔄 AUTO-CREATE MODE ACTIVATED!')
              console.log('  - Error indicates user not found')
              console.log('  - Auto-create flag is enabled')
              console.log('  - Attempting to create user account...')
              toast.info('User not found, creating account automatically...')

              try {
                // Extract email from error message or make API call to get it
                console.log('🔍 Extracting user email from error message...')
                const emailMatch = callbackError.message.match(/email\s+([^\s]+)/)
                const email = emailMatch ? emailMatch[1] : ''
                console.log('  - Extracted email:', email)

                if (!email) {
                  throw new Error('Could not extract email from error message')
                }

                // Auto-generate user details from email
                console.log('🏗️ Auto-generating user details from email...')
                const emailParts = email.split('@')[0].split('.')
                const firstName = emailParts[0] ? capitalize(emailParts[0]) : 'SSO'
                const lastName = emailParts[1] ? capitalize(emailParts[1]) : 'User'
                const username = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
                
                console.log('  - Generated firstName:', firstName)
                console.log('  - Generated lastName:', lastName)
                console.log('  - Generated username:', username)

                const userData = {
                  email: email.toLowerCase(),
                  organizationId,
                  firstName,
                  lastName,
                  username,
                  ssoProvider: storedProvider
                }

                // Create user via new SSO endpoint
                console.log('🔄 Calling createSsoUser API...')
                console.log('  - Endpoint: POST /auth/sso-create-user')
                console.log('  - Payload:', userData)
                
                const createResult = await createSsoUser(userData)

                console.log('✅ USER CREATED SUCCESSFULLY!')
                console.log('  - New user:', createResult.user)
                console.log('  - Message:', createResult.message)
                toast.success(`Account created for ${email}! Retrying SSO login...`)

                // Wait a moment then retry SSO callback
                console.log('⏳ Waiting 2 seconds before retrying SSO login...')
                setTimeout(async () => {
                  try {
                    console.log('🔄 RETRYING SSO LOGIN with new account...')
                    console.log('  - Endpoint: POST /auth/oidc/callback')
                    console.log('  - Same code and state as before')
                    
                    const retryLoginResponse = await processOidcCallback(code, state, organizationId, storedProvider)

                    console.log('✅ SSO RETRY LOGIN SUCCESSFUL!')
                    console.log('  - User Info:', retryLoginResponse.userInfo)
                    console.log('  - Token received:', retryLoginResponse.token.substring(0, 20) + '...')

                    // Store authentication data
                    authStore.accessToken = retryLoginResponse.token
                    authStore.userDetail = retryLoginResponse.userInfo
                    localStorage.setItem('ACCESS_TOKEN', retryLoginResponse.token)

                    console.log('💾 Authentication data stored for new user')
                    toast.success(`Welcome ${retryLoginResponse.userInfo.firstName}! Login successful with new account.`)

                    // Clear SSO temporary data
                    clearSSOData()
                    console.log('🧹 Cleared SSO temporary data')

                    // Redirect to dashboard
                    console.log('🏠 Redirecting to dashboard with new account...')
                    console.log('═══════════════════════════════════════════════════════════')
                    navigate('/dashboard', { replace: true })

                  } catch (retryError: any) {
                    console.error('❌ SSO Retry failed:', retryError)
                    toast.error(`Failed to login after account creation: ${retryError.message}`)
                    clearSSOData()
                    navigate('/login', { replace: true })
                  }
                }, 2000)

              } catch (createError: any) {
                console.error('❌ User creation failed:', createError)
                toast.error(`Failed to create account: ${createError.message}`)
                clearSSOData()
                navigate('/login', { replace: true })
              }
            } else {
              // Not auto-create mode or different error
              throw callbackError
            }
          }

        } catch (error: any) {
          console.error('❌ SSO Callback Error:', error)
          toast.error(`SSO Authentication failed: ${error.message}`)
          
          // Clear SSO data and redirect to login
          clearSSOData()
          navigate('/login', { replace: true })
        }
      }
    }

    // Only handle callback if we have the necessary parameters
    if (location.search.includes('code=') || location.search.includes('error=') || location.search.includes('auth=')) {
      handleSSOCallback()
    }
  }, [location, navigate, authStore])

  const clearSSOData = () => {
    localStorage.removeItem('sso-state')
    localStorage.removeItem('sso-org-id')
    localStorage.removeItem('sso-provider')
    localStorage.removeItem('sso-auto-create')
  }

  // Helper function to capitalize first letter
  const capitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  return {
    clearSSOData
  }
}

export default useSSO 